import queryString from 'querystring';

import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import assets from '@wetransfer/transfer/assets';
import VideoIllustration from '@wetransfer/transfer/component/illustration/video-illustration';
import concordeCookie from '@wetransfer/transfer/modules/concorde-cookie';
import Router from '@wetransfer/transfer/modules/router';
import { Button } from '@wetransfer/transfer/oneui/react';
import { selectAuth0UserId } from '@wetransfer/transfer/store/selector/auth';
import { selectCountryCode } from '@wetransfer/transfer/store/selector/session';
import { selectUserPlanTier } from '@wetransfer/transfer/store/selector/user';

import { DID_SEE_PANEL_COOKIE_KEY, SURVEY_URL } from './constants';
import styles from './PaidTransfersBuyerSurveyPage.module.scss';

export default function PaidTransfersBuyerSurveyPage(): JSX.Element {
  // Close panel if already seen
  useEffect(() => {
    if (concordeCookie.get(DID_SEE_PANEL_COOKIE_KEY)) {
      Router.navigateTo('/', true);
    }
  }, []);

  useEffect(() => {
    concordeCookie.set(DID_SEE_PANEL_COOKIE_KEY, 'true', { days: 365 });
  }, []);

  const auth0UserId = useSelector(selectAuth0UserId);
  const planTier = useSelector(selectUserPlanTier) || 'anonymous';
  const countryCode = useSelector(selectCountryCode);

  const [didSubmitAnswer, setDidSubmitAnswer] = useState(false);

  const handleClickAccept = useCallback(() => {
    const typeformHiddenFields = queryString.stringify({
      c: countryCode,
      s: planTier,
      i: auth0UserId,
    });

    const url = `${SURVEY_URL}#${typeformHiddenFields}`;
    window.open(url, '_blank', 'noopener,noreferrer');

    // Open survey in new tab
    window.open(
      `${SURVEY_URL}?${typeformHiddenFields}`,
      '_blank',
      'noopener,noreferrer'
    );

    setTimeout(() => {
      setDidSubmitAnswer(true);
    }, 500);
  }, [planTier, auth0UserId, countryCode]);

  const closePanel = useCallback(() => {
    Router.navigateTo('/', true);
  }, []);

  if (didSubmitAnswer) {
    return (
      <div className={classNames(styles.panel, styles.thanks)}>
        <VideoIllustration
          className={styles.thanksVideoIllustration}
          src="transfer_window/transfer_completed-1"
          type="transfer-complete-upload"
        />
        <h1 className={styles.h1}>
          Thanks for
          <br />
          your help!
        </h1>
        <p className={styles.body}>
          That’s everything we wanted to know for now.
          <br />
          Have a great day!
        </p>
        <Button
          small
          variant="primary"
          label="Close panel"
          onClick={closePanel}
          key="close-panel"
        />
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <img
        className={styles.illustration}
        src={assets.lookup('01-wetransfer-terms.png')}
        alt="illustration"
      />
      <h1 className={styles.h1}>How did we do?</h1>
      <h2 className={styles.h2}>
        We’d love to hear about your experience of purchasing files <br />
        via WeTransfer, and any thoughts on how we could make it better.
      </h2>
      <p className={styles.body}>Please fill out this (very) short survey.</p>
      <div className={styles.buttons}>
        <Button
          className={styles.fixedWidthButton}
          variant="primary"
          label="Sure!"
          onClick={handleClickAccept}
        />
        <Button
          className={styles.fixedWidthButton}
          variant="secondary"
          label="Not today"
          onClick={closePanel}
        />
      </div>
    </div>
  );
}
